import type {SketchDimensionOrientation} from '../SketchDimensionTypes';
import type {SmartDimensionVec2} from './SketchSmartDimensionTypes';

/**
 * 两线角域解析结果。
 */
export interface SmartDimensionAngleZoneResolution {
    /** 标注目标角（弧度，幅值；锐角域=|α|，补角域=π-|α|）。 */
    readonly targetAngle: number;
    /** 象限分支符号（方程 branchSign）。 */
    readonly branchSign: 1 | -1;
    /** 角象限（0-3）：0=d1→d2 主角域，1=补角域，2=对顶主角域，3=对顶补角域。 */
    readonly quadrant: 0 | 1 | 2 | 3;
}

/**
 * 智能尺寸放置解析（纯函数）。
 *
 * 设计说明：
 * 1. 指针区域 → 方向候选/角度象限的判定逻辑集中于此，推断策略只消费结论；
 * 2. 候选扇区角度、像素容差与滞回阈值是 V 级待实机项（01 基线 §15.1/15.2），
 *    当前实现为 I 级推断：两点方向按指针相对连线中点的偏移主轴切换，
 *    连线方向 ±22.5° 锥区内为 aligned；实机金标后只需调整本文件阈值。
 */
export class SketchDimensionPlacementResolver {
    /** aligned 判定锥区半角（弧度，±22.5°）。 */
    private static readonly ALIGNED_CONE_HALF_ANGLE = Math.PI / 8;

    /**
     * 解析两点距离的方向候选。
     *
     * @param first 第一点（局部 2D）。
     * @param second 第二点（局部 2D）。
     * @param pointer 指针位置（局部 2D）。
     * @returns 方向候选。
     */
    public static resolveTwoPointOrientation(
        first: SmartDimensionVec2,
        second: SmartDimensionVec2,
        pointer: SmartDimensionVec2,
    ): SketchDimensionOrientation {
        const midX = (first[0] + second[0]) / 2;
        const midY = (first[1] + second[1]) / 2;
        const offsetX = pointer[0] - midX;
        const offsetY = pointer[1] - midY;
        const chordX = second[0] - first[0];
        const chordY = second[1] - first[1];
        const chordLength = Math.hypot(chordX, chordY);
        const offsetLength = Math.hypot(offsetX, offsetY);

        // 指针几乎贴着中点或连线退化时，退回 aligned（绝对距离）。
        if (chordLength < 1e-12 || offsetLength < 1e-12) {
            return 'aligned';
        }

        // 连线为纯水平/纯竖直时，aligned 与单轴距离重合，直接按偏移主轴给正交候选。
        const chordIsAxisAligned = Math.abs(chordX) < 1e-12 || Math.abs(chordY) < 1e-12;

        if (!chordIsAxisAligned) {
            /**
             * 指针方向落在连线方向 ±22.5° 锥区（含反向）内 → aligned：
             * 对应 SOLIDWORKS 沿两点连线拖动时出现的平行（linear）尺寸候选。
             */
            const cosine = Math.abs((offsetX * chordX + offsetY * chordY) / (offsetLength * chordLength));

            if (cosine > Math.cos(SketchDimensionPlacementResolver.ALIGNED_CONE_HALF_ANGLE)) {
                return 'aligned';
            }
        }

        /**
         * 指针偏移主轴决定单轴候选：上下区域标注水平距离、左右区域标注竖直距离
         * （尺寸线放在指针侧，测量方向与放置方向正交）。
         */
        return Math.abs(offsetY) >= Math.abs(offsetX) ? 'horizontal' : 'vertical';
    }

    /**
     * 解析两线角度的象限候选。
     *
     * 象限语义（对照 swSmartDimensionDirection_e 四向）：以两线交点为原点，
     * 指针落在 d1→d2 扇区（含对顶）标注主角 |α|；落在补角扇区标注 π-|α|。
     *
     * @param firstDirection 第一线单位方向。
     * @param secondDirection 第二线单位方向。
     * @param intersection 两线交点（局部 2D）。
     * @param pointer 指针位置（局部 2D）。
     * @returns 角域解析结果；两线平行（无交点语义）返回 null。
     */
    public static resolveAngleZone(
        firstDirection: SmartDimensionVec2,
        secondDirection: SmartDimensionVec2,
        intersection: SmartDimensionVec2,
        pointer: SmartDimensionVec2,
    ): SmartDimensionAngleZoneResolution | null {
        const cross = firstDirection[0] * secondDirection[1] - firstDirection[1] * secondDirection[0];
        const dot = firstDirection[0] * secondDirection[0] + firstDirection[1] * secondDirection[1];
        const signedAngle = Math.atan2(cross, dot);

        if (Math.abs(signedAngle) < 1e-9 || Math.abs(Math.abs(signedAngle) - Math.PI) < 1e-9) {
            return null;
        }

        const pointerX = pointer[0] - intersection[0];
        const pointerY = pointer[1] - intersection[1];

        if (Math.hypot(pointerX, pointerY) < 1e-12) {
            // 指针贴住交点：默认主角域。
            return {
                targetAngle: Math.abs(signedAngle),
                branchSign: signedAngle >= 0 ? 1 : -1,
                quadrant: 0,
            };
        }

        /**
         * 用指针方向相对两线的半平面符号判定角域：
         * s1 = 指针在 d1 的哪一侧（沿 α 旋转方向为正），s2 = 相对 d2。
         * 主角域（d1→d2 之间，含对顶）：s1 与 s2 异号；补角域：同号。
         */
        const rotationSign = signedAngle >= 0 ? 1 : -1;
        const sideOfFirst = Math.sign(
            (firstDirection[0] * pointerY - firstDirection[1] * pointerX) * rotationSign,
        );
        const sideOfSecond = Math.sign(
            (secondDirection[0] * pointerY - secondDirection[1] * pointerX) * rotationSign,
        );
        const inPrimaryZone = sideOfFirst >= 0 && sideOfSecond <= 0;
        const inOppositePrimaryZone = sideOfFirst <= 0 && sideOfSecond >= 0;

        if (inPrimaryZone || inOppositePrimaryZone) {
            return {
                targetAngle: Math.abs(signedAngle),
                branchSign: rotationSign as 1 | -1,
                quadrant: inPrimaryZone ? 0 : 2,
            };
        }

        /**
         * 补角域：目标角 π-|α|；求解分支保持沿 α 同向旋转符号，
         * LM 从当前 seed 出发会收敛到把夹角推到补角值的最近解。
         */
        return {
            targetAngle: Math.PI - Math.abs(signedAngle),
            branchSign: rotationSign as 1 | -1,
            quadrant: sideOfFirst > 0 ? 1 : 3,
        };
    }

    /**
     * 求两条无限直线交点。
     *
     * @param firstStart 第一线起点。
     * @param firstEnd 第一线终点。
     * @param secondStart 第二线起点。
     * @param secondEnd 第二线终点。
     * @returns 交点（局部 2D）；平行时返回 null。
     */
    public static intersectLines(
        firstStart: SmartDimensionVec2,
        firstEnd: SmartDimensionVec2,
        secondStart: SmartDimensionVec2,
        secondEnd: SmartDimensionVec2,
    ): SmartDimensionVec2 | null {
        const d1x = firstEnd[0] - firstStart[0];
        const d1y = firstEnd[1] - firstStart[1];
        const d2x = secondEnd[0] - secondStart[0];
        const d2y = secondEnd[1] - secondStart[1];
        const denominator = d1x * d2y - d1y * d2x;

        if (Math.abs(denominator) < 1e-15) {
            return null;
        }

        const t = ((secondStart[0] - firstStart[0]) * d2y - (secondStart[1] - firstStart[1]) * d2x) / denominator;

        return [firstStart[0] + t * d1x, firstStart[1] + t * d1y];
    }

    /**
     * 判断两线是否近似平行。
     *
     * 说明：近似平行阈值是 V 级待实机项（SD-SEL-004），当前取单位方向叉积
     * 幅值 < 1e-3（约 0.057°）。
     *
     * @param firstDirection 第一线单位方向。
     * @param secondDirection 第二线单位方向。
     * @returns 是否近似平行。
     */
    public static areDirectionsParallel(
        firstDirection: SmartDimensionVec2,
        secondDirection: SmartDimensionVec2,
    ): boolean {
        return Math.abs(firstDirection[0] * secondDirection[1] - firstDirection[1] * secondDirection[0]) < 1e-3;
    }
}
